import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';
import { avatars } from '@/config/siteImages';

export default function WhyUsPage() {
  useLanguageFromPath();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);

  const mentors = [
    {
      name: t('about_mentor_1_name'),
      role: t('about_mentor_1_role'),
      exp: t('about_mentor_1_exp'),
      bio: t('about_mentor_1_bio'),
      avatar: avatars.small.mentor1,
      badges: [t('about_mentor_badge_lending'), t('about_mentor_badge_smb'), t('about_mentor_badge_risk')],
    },
    {
      name: t('about_mentor_2_name'),
      role: t('about_mentor_2_role'),
      exp: t('about_mentor_2_exp'),
      bio: t('about_mentor_2_bio'),
      avatar: avatars.small.mentor2,
      badges: [t('about_mentor_badge_strategy'), t('about_mentor_badge_latino'), t('about_mentor_badge_bilingual')],
    },
  ];

  const values = [
    { icon: 'ri-eye-2-line', title: t('why_val_1_title'), desc: t('why_val_1_desc') },
    { icon: 'ri-user-heart-line', title: t('why_val_2_title'), desc: t('why_val_2_desc') },
    { icon: 'ri-shield-check-line', title: t('why_val_3_title'), desc: t('why_val_3_desc') },
    { icon: 'ri-global-line', title: t('why_val_4_title'), desc: t('why_val_4_desc') },
  ];

  const oldWayRows = [
    [t('why_row_interest'), t('why_old_interest')],
    [t('why_row_time'), t('why_old_time')],
    [t('why_row_support'), t('why_old_support')],
    [t('why_row_fees'), t('why_old_fees')],
    [t('why_row_flexibility'), t('why_old_flexibility')],
    [t('why_row_transparency'), t('why_old_transparency')],
    [t('why_row_language'), t('why_old_language')],
  ];

  const cpRows = [
    [t('why_row_interest'), t('why_cp_interest')],
    [t('why_row_time'), t('why_cp_time')],
    [t('why_row_support'), t('why_cp_support')],
    [t('why_row_fees'), t('why_cp_fees')],
    [t('why_row_flexibility'), t('why_cp_flexibility')],
    [t('why_row_transparency'), t('why_cp_transparency')],
    [t('why_row_language'), t('why_cp_language')],
  ];

  const stats = [
    { value: '15+', label: t('stat_3_label'), icon: 'ri-time-line' },
    { value: '50+', label: t('stat_4_label'), icon: 'ri-user-star-line' },
    { value: '$47M+', label: t('stat_1_label'), icon: 'ri-funds-box-line' },
    { value: '2,400+', label: t('stat_2_label'), icon: 'ri-store-2-line' },
  ];

  useEffect(() => {
    document.title = 'Why Choose Kailac | Expert-Guided 0% Business Funding Advisory';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Kailac offers 15+ years of experience, mentors with 50+ years in finance, radical transparency, dedicated human advisors, and full bilingual EN/ES support. Not a lender — your expert advocate. For qualified applicants.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('why_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('why_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('why_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            {t('why_hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">{t('why_compare_title')}</h2>
            <p className="text-base text-gray-500">{t('why_compare_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Old Way */}
            <div className="bg-[#F8F3FB] rounded-2xl p-8 border border-[#E9D9F2]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50">
                  <i className="ri-close-circle-line text-red-500 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-700">{t('why_old_label')}</h3>
              </div>
              {oldWayRows.map(([label, value]) => (
                <div key={label} className="flex items-start gap-3 mb-3">
                  <i className="ri-close-circle-fill text-red-400 text-sm mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">{label}: </span>
                    <span className="text-sm text-gray-700">{value}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Kailac Way */}
            <div className="bg-[#F8F3FB] border-2 border-[#4F2D63] rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E9D9F2]">
                  <i className="ri-check-double-line text-[#4F2D63] text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('why_cp_label')}</h3>
              </div>
              {cpRows.map(([label, value]) => (
                <div key={label} className="flex items-start gap-3 mb-3">
                  <i className="ri-check-line text-[#0F766E] text-sm mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">{label}: </span>
                    <span className="text-sm text-gray-900 font-medium">{value}</span>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-400 italic mt-4 pt-4 border-t border-[#E9D9F2]">{t('why_cp_disclaimer')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map(({ value, label, icon }) => (
              <div key={label} className="bg-white rounded-2xl p-6 border border-[#E9D9F2] text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8F3FB] mx-auto mb-3">
                  <i className={`${icon} text-[#4F2D63] text-lg`}></i>
                </div>
                <p className="text-4xl font-bold text-gray-900 mb-1">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">{t('why_values_title')}</h2>
            <p className="text-base text-gray-500">{t('why_values_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {values.map(({ icon, title, desc }) => (
              <div key={title} className="flex items-start gap-5 bg-[#F8F3FB] rounded-2xl p-6 border border-[#E9D9F2]">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#E9D9F2] flex-shrink-0">
                  <i className={`${icon} text-[#4F2D63] text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mentors */}
      <section className="py-20 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('about_mentors_eyebrow')}</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">{t('about_mentors_title')}</h2>
            <p className="text-base text-gray-500 max-w-xl mx-auto">{t('about_mentors_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mentors.map(({ name, role, exp, bio, avatar, badges }) => (
              <div key={name} className="bg-white border border-[#E9D9F2] rounded-2xl p-7">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <img src={avatar} alt={name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{name}</h3>
                    <p className="text-sm text-[#4F2D63]">{role}</p>
                    <p className="text-xs text-amber-600 font-semibold mt-1">{exp}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{bio}</p>
                <div className="flex flex-wrap gap-2">
                  {badges.map((b) => (
                    <span key={b} className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-2.5 py-1 rounded-full border border-[#E9D9F2]">{b}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('why_cta_title')}</h2>
          <p className="text-gray-500 mb-8 text-base">{t('why_cta_subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={lp('/contact', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#4F2D63] text-white font-semibold hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap">
              {t('why_cta_primary')} <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to={lp('/about', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              {t('why_cta_secondary')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
